import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/**
 * State for the drafting feature. A draft is an artifact version (today, the latest COMPLETE
 * `md` version of an artifact); the records themselves live in the shared artifacts store,
 * keyed by artifact version id.
 */
export type DraftingState = {
  /* Artifact version ids of each matter's drafts */
  draftArtifactVersionIdsByMatterId: Partial<{ [matterId: string]: string[] }>
  /* Per-matter fetch state */
  lastRefreshByMatterId: Partial<{ [matterId: string]: number }>
  loadingByMatterId: Partial<{ [matterId: string]: boolean }>
  errorByMatterId: Partial<{ [matterId: string]: boolean }>
  /* The conversation each draft's artifact version was created in */
  conversationIdByDraftArtifactVersionId: Partial<{ [draftArtifactVersionId: string]: string }>
}

const initialState: DraftingState = {
  draftArtifactVersionIdsByMatterId: {},
  lastRefreshByMatterId: {},
  loadingByMatterId: {},
  errorByMatterId: {},
  conversationIdByDraftArtifactVersionId: {},
}

/**
 * `lastRefreshByMatterId` value marking a matter's drafts stale: older than any real fetch time, so the
 * cache check treats it as expired. A successful fetch overwrites it. Read via `selectDraftsStaleForMatter`.
 */
export const kStaleDraftsTimestamp = 0

export const draftingSlice = createSlice({
  name: 'draftingState',
  initialState,
  reducers: {
    // ============== Listener Middleware Triggers ============== >
    refreshMatterDrafts: (_state, _action: PayloadAction<{ matterId: string; forceRefresh: boolean }>) => {
      // Handled by listener middleware
    },

    // ============== Slice Actions ============== >
    nullifyData: () => initialState,

    setDraftArtifactVersionIdsForMatter: (state, action: PayloadAction<{ matterId: string; artifactVersionIds: string[] }>) => {
      state.draftArtifactVersionIdsByMatterId[action.payload.matterId] = action.payload.artifactVersionIds
    },

    setDraftArtifactVersionConversationIds: (state, action: PayloadAction<Partial<{ [draftArtifactVersionId: string]: string }>>) => {
      Object.assign(state.conversationIdByDraftArtifactVersionId, action.payload)
    },

    setLoadingForMatter: (state, action: PayloadAction<{ matterId: string; loading: boolean }>) => {
      state.loadingByMatterId[action.payload.matterId] = action.payload.loading
    },

    setErrorForMatter: (state, action: PayloadAction<{ matterId: string; error: boolean }>) => {
      state.errorByMatterId[action.payload.matterId] = action.payload.error
    },

    setLastRefreshForMatter: (state, action: PayloadAction<{ matterId: string; timestamp: number }>) => {
      state.lastRefreshByMatterId[action.payload.matterId] = action.payload.timestamp
    },

    /** Invalidates a matter's drafts cache so the next refresh refetches it. */
    invalidateMatterDrafts: (state, action: PayloadAction<{ matterId: string }>) => {
      // No-op for a never-loaded matter: nothing is cached to invalidate, and its first visit fetches fresh.
      if (state.lastRefreshByMatterId[action.payload.matterId] === undefined) return
      // Mark stale rather than delete the entry, so the matter still reads as already-loaded.
      state.lastRefreshByMatterId[action.payload.matterId] = kStaleDraftsTimestamp
    },
  },
})

// Actions
export const DraftingActions = draftingSlice.actions

export default draftingSlice.reducer
